/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useContext, useEffect, useState } from 'react'
import { CtaAriaLabels, MarketConfig, useDcxChat } from '@dcxiqos/iqos-com-chat'
import {
  IconButton as UIIconButton,
  IconButtonRole,
  IconButtonSize
} from '@dcxwam/dcx-wam-ui-foundations'
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager'
import { useCookies } from 'react-cookie'
import { ChatIconButtonActionProps } from './ChatIconButtonActionProps'
import {
  useInitIsContentAriaHidden,
  useInitLayoutBottomShift
} from '../../../utils/app-layout'
import {
  ChatConfigType,
  GeneralConfigType,
  PageConfig
} from '../../../utils/page-config'
import { GIGYA_COOKIE_UID } from '../../../utils/constants'
import { withErrorBoundary } from '../ErrorBoundary'
import { UserDataContext } from '../../../services/user/user-data'

/** XF surgeon banner: `src` contains "surgeon" (case-insensitive) — measure height for chat offset */
const SURGEON_WARNING_IMG_SELECTOR = 'img[src*="surgeon" i]'

const getConfig = (
  cookies: Record<string, string>,
  userData: Record<string, string>,
  aemChatConfig: ChatConfigType & CtaAriaLabels & GeneralConfigType
): Omit<
  MarketConfig,
  | 'channel'
  | 'counterConsumerIndex'
  | 'scrollFocusRequired'
  | 'preEngagementSetting'
  | 'isRTL'
  | 'proactiveChatInitialized'
  | 'messageButtons'
  | 'fiveStarsRating'
  | 'openingHoursThrottle'
  | 'pageEntry'
> => ({
  userID: cookies?.gig_uid ? window.atob(cookies.gig_uid) : 'anonymous',
  friendlyName: userData?.friendlyName ? userData.friendlyName : 'Anonymous',
  market: aemChatConfig?.market || '',
  language: aemChatConfig?.lang || '',
  flexFlowSid: aemChatConfig?.flexFlowSid || '',
  accountSid: aemChatConfig?.accountSid || '',
  runtimeDomain: aemChatConfig?.twilio?.runtimeDomain || '',
  noCache: true,
  enableLogs: true,
  containerOffset: '8em',
  liveChatEnabled: aemChatConfig?.liveChatEnabled || false,
  timezone: aemChatConfig?.timezone || '',
  twilioScript: aemChatConfig?.twilio?.deploymentUrl || '',
  websiteOrigin: aemChatConfig?.websiteOrigin || window?.location?.origin,
  productBrandFamily: aemChatConfig?.productBrandFamily || '',
  allowFullScreen: aemChatConfig?.twilio?.allowFullScreenMobile || false,
  ctaAriaLabels: {
    attachFileCtaAriaLabel: aemChatConfig?.attachFileCtaAriaLabel,
    closeChatCtaAriaLabel: aemChatConfig?.closeChatCtaAriaLabel,
    messageInputAriaLabel: aemChatConfig?.messageInputAriaLabel,
    sendMessageCtaAriaLabel: aemChatConfig?.sendMessageCtaAriaLabel,
    startChatCtaAriaLabel: aemChatConfig?.startChatCtaAriaLabel
  }
})

const Chat = ({
  userData,
  aemChatConfig
}: {
  userData: any
  aemChatConfig: ChatConfigType & CtaAriaLabels & GeneralConfigType
}) => {
  const [cookies] = useCookies([GIGYA_COOKIE_UID])
  const bottomShift = useInitLayoutBottomShift()
  const [surgeonImagePx, setSurgeonImagePx] = useState(0)
  const ariaHidden = useInitIsContentAriaHidden()

  useEffect(() => {
    let observedImg: HTMLImageElement | null = null
    let resizeObserver: ResizeObserver | null = null

    const onImgLoad = () => {
      const el = document.querySelector<HTMLImageElement>(
        SURGEON_WARNING_IMG_SELECTOR
      )
      setSurgeonImagePx(el ? el.offsetHeight : 0)
    }

    const cleanupImgObserver = () => {
      if (resizeObserver && observedImg) {
        resizeObserver.disconnect()
        observedImg.removeEventListener('load', onImgLoad)
        resizeObserver = null
        observedImg = null
      }
    }

    const updateHeight = () => {
      const img = document.querySelector<HTMLImageElement>(
        SURGEON_WARNING_IMG_SELECTOR
      )
      setSurgeonImagePx(img ? img.offsetHeight : 0)

      if (img !== observedImg) {
        cleanupImgObserver()
        observedImg = img
        if (img) {
          resizeObserver = new ResizeObserver(onImgLoad)
          resizeObserver.observe(img)
          img.addEventListener('load', onImgLoad)
        }
      }
    }

    updateHeight()

    const mutationObserver = new MutationObserver(updateHeight)
    mutationObserver.observe(document.body, { subtree: true, childList: true })

    return () => {
      mutationObserver.disconnect()
      cleanupImgObserver()
    }
  }, [])
  const config = useMemo(
    () => getConfig(cookies, userData, aemChatConfig) as MarketConfig,
    [userData]
  )
  const { onClickChatIcon, isEnabled, isLoading } = useDcxChat(config)

  useEffect(() => {
    ChatIconButtonActionProps.actionProps.onClick = onClickChatIcon
  }, [onClickChatIcon])

  const buttonConfig = {
    onClick: onClickChatIcon,
    iconButtonRole: IconButtonRole.Button,
    icon: 'pmi-icon-comment',
    size: IconButtonSize.Large,
    'aria-label': aemChatConfig?.ariaLabel || '',
    loading: isLoading,
    ...(ariaHidden ? { 'aria-hidden': true } : {}),
    sx: {
      position: 'fixed',
      bottom: `calc(1.5rem + ${(bottomShift || 0) + surgeonImagePx}px)`,
      insetInlineEnd: '1rem',
      zIndex: 900
    }
  }
  return isEnabled ? <UIIconButton {...buttonConfig} /> : null
}

const ChatWithErrorBoundary = withErrorBoundary(Chat, {
  componentName: 'Chat'
})

const ChatIconButton = () => {
  const config = PageConfig?.getConfig() as GeneralConfigType
  const userData = useContext(UserDataContext)
  const friendlyName = [
    userData?.user?.profile?.firstName || '',
    userData?.user?.profile?.lastName || ''
  ]
    .filter(Boolean)
    .join(' ')
  const userName = friendlyName
    ? {
        friendlyName
      }
    : null
  const showChatButton =
    Object.keys(config || {}).length !== 0 &&
    Object.keys(config?.chat || {}).length !== 0 &&
    !!userData
  return !AuthoringUtils.isInEditor() && showChatButton ? (
    <ChatWithErrorBoundary
      userData={userName}
      aemChatConfig={{
        market: config?.market,
        lang: config?.lang,
        ...config?.chat
      }}
    />
  ) : null
}

export default ChatIconButton
